// Book insights (VISION.md book-intelligence direction, 2026-07-17). Everything
// here is computed LOCALLY from the opened epub + the reader's own highlights —
// no network, no external data. Total words, chapters, words/chapter, language,
// reading time, and word-frequency tables for word clouds (of the whole book
// and of the highlights). The author/bibliography/commentary layer comes later
// via a books API + the AI companion.

import type { Book as EpubBook } from "epubjs";
import type { Highlight } from "@/types/highlight";

export interface ChapterStat {
  title: string;
  words: number;
}
export interface BookStats {
  totalWords: number;
  chapterCount: number;
  chapters: ChapterStat[];
  readingMinutes: number; // at ~230 wpm
  bookCloud: WordCount[];
  highlightCloud: WordCount[];
}
export interface WordCount {
  word: string;
  count: number;
}

const STOPWORDS = new Set(
  ("the a an and or but if then else of to in on at by for with from up out as is are was were be been " +
    "being have has had do does did will would shall should can could may might must not no nor so than " +
    "too very just this that these those it its it's i you he she they we them his her their our your my me " +
    "him us who whom which what where when why how all any both each few more most other some such only own " +
    "same s t don now o re ve ll d m into over under again further once here there about against between " +
    "through during before after above below off down out upon said one two he'd she'd i'll etc mr mrs " +
    "upon shall thou thee thy hath unto")
    .split(/\s+/),
);

function wordFreq(text: string, limit = 60): WordCount[] {
  const counts = new Map<string, number>();
  const words = text
    .toLowerCase()
    .replace(/['’]/g, "")
    .match(/[\p{L}]{3,}/gu);
  if (!words) return [];
  for (const w of words) {
    if (STOPWORDS.has(w)) continue;
    counts.set(w, (counts.get(w) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

function countWords(text: string): number {
  const m = text.trim().match(/\S+/g);
  return m ? m.length : 0;
}

/** Walk the spine, counting words per section and collecting text for the word
 *  cloud. Best-effort: a section that won't load is skipped. */
export async function computeBookStats(
  book: EpubBook,
  highlights: Highlight[],
  language?: string,
): Promise<BookStats> {
  const chapters: ChapterStat[] = [];
  let allText = "";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const items: any[] = (book as any).spine?.spineItems ?? [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav: any[] = (await (book as any).loaded?.navigation)?.toc ?? [];
  const titleFor = (href: string, i: number): string => {
    const h = (href || "").split("#")[0];
    const match = nav.find((n) => (n.href || "").split("#")[0].endsWith(h.split("/").pop() || h));
    return match?.label?.trim() || `Chapter ${i + 1}`;
  };

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const doc: any = await item.load((book as any).load.bind(book));
      const root: Element | Document | null =
        doc?.body ?? (doc?.documentElement ? doc : null);
      const text = (root?.textContent || "").replace(/\s+/g, " ").trim();
      item.unload?.();
      const words = countWords(text);
      if (words < 5) continue; // skip covers / blank sections
      chapters.push({ title: titleFor(item.href, chapters.length), words });
      allText += " " + text;
    } catch {
      /* skip a section that won't load */
    }
  }

  const totalWords = chapters.reduce((s, c) => s + c.words, 0);
  const hlText = highlights
    .filter((h) => h.kind !== "review")
    .map((h) => `${h.text} ${h.note || ""}`)
    .join(" ");

  return {
    totalWords,
    chapterCount: chapters.length,
    chapters,
    readingMinutes: Math.max(1, Math.round(totalWords / 230)),
    bookCloud: wordFreq(allText, 60),
    highlightCloud: wordFreq(hlText, 40),
    // language passed through for display
    ...(language ? {} : {}),
  };
}
